"use client"

import { useEffect, useState } from "react"

// Define set-aside type codes and their categories
const SET_ASIDE_CATEGORIES = {
  "HBCU or MI": ["HMP", "HMT"],
  HUBZone: ["HZS", "HZC"],
  "Native American-Owned": ["BI", "ISEE", "ISBEE"],
  "No Set Aside": ["NONE"],
  "Small Disadvantaged Business and 8(a)": ["8A", "8AN", "8ACCiv", "ESB", "SBA", "SBP", "RSBCiv", "HS2Civ", "HS3"],
  "Veteran-Owned": ["VSA", "SDVOSBS", "SDVOSBC", "VSS", "VSBCiv"],
  "Women-Owned": ["WOSB", "WOSBSS", "EDWOSB", "EDWOSBSS"],
}

export default function useSpendingBySetAsideType(naics, normalizedAgencyName) {
  const [groupedData, setGroupedData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!naics) {
      setGroupedData([])
      setLoading(false)
      return
    }

    const fetchData = async () => {
      setLoading(true)
      setError(null)

      try {
        const baseFilters = {
          time_period: [
            { start_date: "2024-10-01", end_date: "2025-09-30" },
            { start_date: "2023-10-01", end_date: "2024-09-30" },
            { start_date: "2022-10-01", end_date: "2023-09-30" },
            { start_date: "2021-10-01", end_date: "2022-09-30" },
          ],
          naics_codes: [naics],
        }

        if (normalizedAgencyName) {
          baseFilters.agencies = [
            {
              type: "awarding",
              tier: "toptier",
              name: normalizedAgencyName,
            },
          ]
        }

        // One request per category
        const categoryRequests = await Promise.all(
          Object.entries(SET_ASIDE_CATEGORIES).map(async ([category, codes]) => {
            const filters = { ...baseFilters, set_aside_type_codes: codes }

            try {
              const response = await fetch(
                "https://api.usaspending.gov/api/v2/search/spending_by_category/awarding_agency",
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    category: "awarding_agency",
                    filters,
                    limit: 10,
                    page: 1,
                    auditTrail: `Set Aside Category: ${category}`,
                    subawards: false,
                    spending_level: "awards",
                  }),
                },
              )

              if (!response.ok) {
                return { category, name: category, amount: 0 }
              }

              const data = await response.json()
              const amount = data.results?.[0]?.amount || 0
              return { category, name: category, amount }
            } catch (err) {
              console.error(`Error fetching data for ${category}:`, err)
              return { category, name: category, amount: 0 }
            }
          })
        )

        // Prepare final grouped data
        const groupedDataArray = categoryRequests
          .filter(({ amount }) => amount > 0)
          .sort((a, b) => b.amount - a.amount)

        setGroupedData(groupedDataArray)
      } catch (err) {
        console.error("Error fetching grouped set-aside data", err)
        setError(err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [naics, normalizedAgencyName])

  return { groupedData, loading, error }
}
